// M5.1 — vendor modal with T4C-style cart UI. Buy column lists the NPC's
// stock; sell column lists the player's inventory (equipped excluded server-
// side anyway). Each row has a qty stepper: − [N] +. Arrows disable when
// the running cart would push past copper budget, weight cap, or maxStack.
// Direct typing in the qty field is supported. One "Validate" button commits
// the whole cart in a single vendorTransact message.

import {
  formatCurrency,
  getItemTemplate,
  type ItemInstance,
  type ItemTemplate,
  sellPriceOf,
  VENDOR_BUY_MAX_PER_LINE,
  type VendorStockEntry,
} from "@vinyl/shared";
import { el } from "./dom.js";
import { bindItemTooltip, hideItemTooltip } from "./item-tooltip.js";
import { setupWindow, type WindowChromeHandle } from "./window-chrome.js";

export interface VendorCartLines {
  buy: { templateId: string; qty: number }[];
  sell: { instanceId: string; qty: number }[];
}

export interface VendorWindowCallbacks {
  onValidate: (npcId: string, lines: VendorCartLines) => void;
  onClose: () => void;
}

export class VendorWindow {
  readonly root: HTMLElement;
  private npcId: string | null = null;
  private vendorName = "Vendor";
  private dialogText = "";
  private stock: VendorStockEntry[] = [];
  private sellMultiplier = 0.25;
  private inventory: ItemInstance[] = [];
  private copper = 0;
  private currentWeight = 0;
  private maxWeight = 100;
  private chrome!: WindowChromeHandle;
  private buyCart = new Map<string, number>(); // templateId -> qty
  private sellCart = new Map<string, number>(); // instanceId -> qty
  // Sell is the default tab — most vendor visits follow a loot run and
  // start with liquidation before browsing the wares.
  private activeTab: "buy" | "sell" = "sell";

  private setVendorTitle!: (text: string) => void;
  private dialogEl: HTMLElement;
  private buyColEl: HTMLElement;
  private sellColEl: HTMLElement;
  private totalsEl: HTMLElement;
  private validateBtn: HTMLButtonElement;
  private buyTabBtn!: HTMLButtonElement;
  private sellTabBtn!: HTMLButtonElement;
  private buyPanel!: HTMLElement;
  private sellPanel!: HTMLElement;
  private sellAllBtn!: HTMLButtonElement;

  constructor(private readonly cb: VendorWindowCallbacks) {
    this.dialogEl = el("p", { class: "vendor-dialog" });
    this.buyColEl = el("div", { class: "vendor-list" });
    this.sellColEl = el("div", { class: "vendor-list" });
    this.totalsEl = el("div", { class: "vendor-totals" });
    this.validateBtn = el(
      "button",
      { class: "btn", type: "button" },
      "Validate",
    ) as HTMLButtonElement;
    this.validateBtn.addEventListener("click", () => this.handleValidate());
    const cancelBtn = el(
      "button",
      { class: "btn-ghost", type: "button" },
      "Cancel",
    ) as HTMLButtonElement;
    cancelBtn.addEventListener("click", () => this.close());

    this.buyTabBtn = el(
      "button",
      { class: "vendor-tab", type: "button", "data-tab": "buy" },
      "Buy",
    ) as HTMLButtonElement;
    this.sellTabBtn = el(
      "button",
      { class: "vendor-tab", type: "button", "data-tab": "sell" },
      "Sell",
    ) as HTMLButtonElement;
    this.buyTabBtn.addEventListener("click", () => this.setActiveTab("buy"));
    this.sellTabBtn.addEventListener("click", () => this.setActiveTab("sell"));

    this.buyPanel = el(
      "section",
      { class: "vendor-col", "data-panel": "buy" },
      this.buyColEl,
    );
    this.sellAllBtn = el(
      "button",
      { class: "btn-ghost vendor-sell-all", type: "button" },
      "Sell all except potions",
    ) as HTMLButtonElement;
    this.sellAllBtn.addEventListener("click", () => this.sellAllExceptPotions());
    this.sellPanel = el(
      "section",
      { class: "vendor-col", "data-panel": "sell" },
      el("div", { class: "vendor-sell-toolbar" }, this.sellAllBtn),
      this.sellColEl,
    );

    this.root = el("aside", { class: "vendor-window hidden" });
    this.chrome = setupWindow({
      windowEl: this.root,
      title: "Vendor",
      storageKey: "vendor",
      onClose: () => this.onClosed(),
    });
    this.setVendorTitle = this.chrome.setTitle;
    this.root.append(
      this.chrome.header,
      this.dialogEl,
      el("div", { class: "vendor-tabs" }, this.sellTabBtn, this.buyTabBtn),
      el("div", { class: "vendor-grid" }, this.buyPanel, this.sellPanel),
      this.totalsEl,
      el("div", { class: "vendor-footer" }, cancelBtn, this.validateBtn),
    );
    this.applyActiveTab();
  }

  private setActiveTab(tab: "buy" | "sell"): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.applyActiveTab();
  }

  private applyActiveTab(): void {
    this.buyTabBtn.classList.toggle("vendor-tab-active", this.activeTab === "buy");
    this.sellTabBtn.classList.toggle("vendor-tab-active", this.activeTab === "sell");
    this.buyPanel.classList.toggle("hidden", this.activeTab !== "buy");
    this.sellPanel.classList.toggle("hidden", this.activeTab !== "sell");
  }

  open(args: {
    npcId: string;
    vendorName: string;
    dialogText: string;
    stock: VendorStockEntry[];
    sellMultiplier: number;
    inventory: ItemInstance[];
    copper: number;
    currentWeight: number;
    maxWeight: number;
  }): void {
    this.npcId = args.npcId;
    this.vendorName = args.vendorName;
    this.dialogText = args.dialogText;
    this.stock = args.stock;
    this.sellMultiplier = args.sellMultiplier;
    this.inventory = args.inventory;
    this.copper = args.copper;
    this.currentWeight = args.currentWeight;
    this.maxWeight = args.maxWeight;
    this.buyCart.clear();
    this.sellCart.clear();
    this.activeTab = "sell";
    this.applyActiveTab();
    this.chrome.open();
    this.setVendorTitle(this.vendorName);
    this.dialogEl.textContent = this.dialogText;
    this.renderAll();
  }

  setInventory(inventory: ItemInstance[]): void {
    if (!this.chrome.isOpen()) return;
    this.inventory = inventory;
    // Drop stale sell-cart lines whose instances no longer exist (e.g. the
    // server completed our previous transact and removed the item).
    for (const id of [...this.sellCart.keys()]) {
      if (!inventory.find((i) => i.instanceId === id)) this.sellCart.delete(id);
    }
    this.renderAll();
  }

  setSelfStats(copper: number, currentWeight: number, maxWeight: number): void {
    if (!this.chrome.isOpen()) return;
    this.copper = copper;
    this.currentWeight = currentWeight;
    this.maxWeight = maxWeight;
    // Constraints changed — re-render so arrow-disable states update. Cart
    // values themselves stay; rows that have become illegal will show a
    // disabled + arrow on next tick.
    this.renderAll();
  }

  // Called when the server reports the vendor is gone (out of range, etc).
  // Closes the window so the user can try again from scratch.
  forceClose(): void {
    this.close();
  }

  isVisible(): boolean {
    return this.chrome.isOpen();
  }

  close(): void {
    this.chrome.close();
  }

  // Fired by the chrome AFTER it hides the window (X button or
  // chrome.close()). Clears the cart + tooltip + notifies the host.
  private onClosed(): void {
    this.npcId = null;
    this.buyCart.clear();
    this.sellCart.clear();
    hideItemTooltip();
    this.cb.onClose();
  }

  private handleValidate(): void {
    if (!this.npcId) return;
    const lines = this.buildLines();
    if (lines.buy.length === 0 && lines.sell.length === 0) return;
    this.cb.onValidate(this.npcId, lines);
  }

  private buildLines(): VendorCartLines {
    const buy: VendorCartLines["buy"] = [];
    const sell: VendorCartLines["sell"] = [];
    for (const [templateId, qty] of this.buyCart) if (qty > 0) buy.push({ templateId, qty });
    for (const [instanceId, qty] of this.sellCart) if (qty > 0) sell.push({ instanceId, qty });
    return { buy, sell };
  }

  // Net cart cost in copper (positive = player pays, negative = player gains).
  private cartCost(): number {
    let total = 0;
    for (const [templateId, qty] of this.buyCart) {
      const tpl = getItemTemplate(templateId);
      if (!tpl) continue;
      total += tpl.value * qty;
    }
    for (const [instanceId, qty] of this.sellCart) {
      const inst = this.inventory.find((i) => i.instanceId === instanceId);
      if (!inst) continue;
      const tpl = getItemTemplate(inst.templateId);
      if (!tpl) continue;
      total -= sellPriceOf(tpl.value, this.sellMultiplier) * qty;
    }
    return total;
  }

  // Weight delta from the cart (added by buys, freed by sells).
  private cartWeightDelta(): number {
    let delta = 0;
    for (const [templateId, qty] of this.buyCart) {
      const tpl = getItemTemplate(templateId);
      if (!tpl) continue;
      delta += tpl.weight * qty;
    }
    for (const [instanceId, qty] of this.sellCart) {
      const inst = this.inventory.find((i) => i.instanceId === instanceId);
      if (!inst) continue;
      const tpl = getItemTemplate(inst.templateId);
      if (!tpl) continue;
      delta -= tpl.weight * qty;
    }
    return delta;
  }

  // True if incrementing the given buy line by 1 would keep the cart valid.
  private canIncBuy(templateId: string): boolean {
    const tpl = getItemTemplate(templateId);
    if (!tpl) return false;
    const cur = this.buyCart.get(templateId) ?? 0;
    // For non-stackables, maxStack=1 governs inventory stacking, not how many
    // a player can buy at the till. Mirror the server's VENDOR_BUY_MAX_PER_LINE.
    const lineCap = tpl.stackable ? tpl.maxStack : VENDOR_BUY_MAX_PER_LINE;
    if (cur + 1 > lineCap) return false;
    const nextCost = this.cartCost() + tpl.value;
    if (nextCost > this.copper) return false;
    const nextWeight = this.currentWeight + this.cartWeightDelta() + tpl.weight;
    // Mirror the server rule: post-transact carried weight must stay below
    // capacity (you can't acquire items once full).
    if (nextWeight >= this.maxWeight) return false;
    return true;
  }

  private canIncSell(instanceId: string): boolean {
    const inst = this.inventory.find((i) => i.instanceId === instanceId);
    if (!inst) return false;
    const cur = this.sellCart.get(instanceId) ?? 0;
    return cur + 1 <= inst.stackCount;
  }

  // A "potion" = a consumable that restores HP or mana. Food (bread) is a
  // consumable too but is NOT spared by the bulk-sell shortcut.
  private isPotion(tpl: ItemTemplate): boolean {
    const kind = tpl.stats.consumable?.kind;
    return kind === "heal" || kind === "restoreManaArc" || kind === "restoreManaDiv";
  }

  // Bulk shortcut: queue every inventory item except potions for sale at its
  // full stack. Rares are included (user's call). Selling only frees weight
  // and gains copper, so no per-line budget check is needed. The cart is still
  // reviewable before Validate, so a stray rare can be deselected by hand.
  private sellAllExceptPotions(): void {
    for (const inst of this.inventory) {
      const tpl = getItemTemplate(inst.templateId);
      if (!tpl || this.isPotion(tpl)) continue;
      this.sellCart.set(inst.instanceId, inst.stackCount);
    }
    this.renderAll();
  }

  private renderAll(): void {
    this.renderBuyColumn();
    this.renderSellColumn();
    this.renderTotals();
    this.renderTabLabels();
    // Nothing to bulk-sell if the bag is empty or holds only potions.
    this.sellAllBtn.disabled = !this.inventory.some((inst) => {
      const tpl = getItemTemplate(inst.templateId);
      return tpl && !this.isPotion(tpl);
    });
  }

  // Surface a small badge on the inactive tab when its cart has lines, so the
  // user doesn't forget e.g. a sell line while wandering the buy tab.
  private renderTabLabels(): void {
    const buyCount = this.buyCart.size;
    const sellCount = this.sellCart.size;
    this.buyTabBtn.textContent = buyCount > 0 ? `Buy · ${buyCount}` : "Buy";
    this.sellTabBtn.textContent = sellCount > 0 ? `Sell · ${sellCount}` : "Sell";
  }

  private renderBuyColumn(): void {
    this.buyColEl.replaceChildren();
    if (this.stock.length === 0) {
      this.buyColEl.append(el("div", { class: "vendor-empty" }, "Nothing for sale."));
      return;
    }
    for (const entry of this.stock) {
      const tpl = getItemTemplate(entry.templateId);
      if (!tpl) continue;
      const qty = this.buyCart.get(entry.templateId) ?? 0;
      const row = this.makeRow({
        name: tpl.name,
        price: entry.unitPrice,
        priceClass: "vendor-price-buy",
        weight: tpl.weight,
        qty,
        maxQty: tpl.stackable ? tpl.maxStack : VENDOR_BUY_MAX_PER_LINE,
        canInc: this.canIncBuy(entry.templateId),
        tooltipTemplate: tpl,
        onChange: (next) => {
          if (next <= 0) this.buyCart.delete(entry.templateId);
          else this.buyCart.set(entry.templateId, next);
          this.renderAll();
        },
      });
      this.buyColEl.append(row);
    }
  }

  private renderSellColumn(): void {
    this.sellColEl.replaceChildren();
    if (this.inventory.length === 0) {
      this.sellColEl.append(el("div", { class: "vendor-empty" }, "Bag is empty."));
      return;
    }
    for (const inst of this.inventory) {
      const tpl = getItemTemplate(inst.templateId);
      if (!tpl) continue;
      const isRare = inst.affixes.length > 0;
      const qty = this.sellCart.get(inst.instanceId) ?? 0;
      const unitPrice = sellPriceOf(tpl.value, this.sellMultiplier);
      const row = this.makeRow({
        name: tpl.name + (isRare ? " ★" : "") + (tpl.stackable && inst.stackCount > 1 ? ` (×${inst.stackCount})` : ""),
        price: unitPrice,
        priceClass: "vendor-price-sell",
        weight: tpl.weight,
        qty,
        maxQty: inst.stackCount,
        canInc: this.canIncSell(inst.instanceId),
        tooltipTemplate: tpl,
        tooltipInstance: inst,
        onChange: (next) => {
          if (next <= 0) this.sellCart.delete(inst.instanceId);
          else this.sellCart.set(inst.instanceId, next);
          this.renderAll();
        },
      });
      if (isRare) row.classList.add("vendor-row-rare");
      this.sellColEl.append(row);
    }
  }

  private renderTotals(): void {
    const cost = this.cartCost();
    const weightDelta = this.cartWeightDelta();
    const projWeight = this.currentWeight + weightDelta;
    const lines: HTMLElement[] = [];
    lines.push(
      el(
        "div",
        { class: "vendor-totals-row" },
        el("span", {}, "Cart cost:"),
        el("span", {}, cost >= 0 ? formatCurrency(cost) : `−${formatCurrency(-cost)}`),
      ),
    );
    lines.push(
      el(
        "div",
        { class: "vendor-totals-row" },
        el("span", {}, "Copper after:"),
        el("span", {}, formatCurrency(this.copper - cost)),
      ),
    );
    lines.push(
      el(
        "div",
        { class: "vendor-totals-row" },
        el("span", {}, "Weight after:"),
        el(
          "span",
          { class: projWeight >= this.maxWeight ? "vendor-bad" : "" },
          `${projWeight.toFixed(1)} / ${this.maxWeight.toFixed(1)} kg`,
        ),
      ),
    );
    this.totalsEl.replaceChildren(...lines);
    const isEmpty = this.buyCart.size === 0 && this.sellCart.size === 0;
    this.validateBtn.disabled = isEmpty;
  }

  private makeRow(args: {
    name: string;
    price: number;
    priceClass: string;
    weight: number;
    qty: number;
    maxQty: number;
    canInc: boolean;
    // M8.x — custom tooltip data. Replaces the old string-via-title attr.
    tooltipTemplate: ItemTemplate;
    tooltipInstance?: ItemInstance;
    onChange: (next: number) => void;
  }): HTMLElement {
    const name = el("div", { class: "vendor-row-name" }, args.name);
    const price = el(
      "div",
      { class: `vendor-row-price ${args.priceClass}` },
      formatCurrency(args.price),
    );
    // Per-line running total in its own column. Empty at qty=0 (CSS hides it
    // via :empty) so unused rows stay quiet.
    const lineTotal = el(
      "div",
      { class: `vendor-row-line-total ${args.priceClass}` },
      args.qty > 0 ? `= ${formatCurrency(args.price * args.qty)}` : "",
    );
    const weight = el("div", { class: "vendor-row-weight" }, `${args.weight}kg`);
    const dec = el("button", { class: "vendor-step", type: "button" }, "−") as HTMLButtonElement;
    const inc = el("button", { class: "vendor-step", type: "button" }, "+") as HTMLButtonElement;
    const qtyInput = el("input", {
      class: "vendor-qty",
      type: "number",
      min: "0",
      max: String(args.maxQty),
      value: String(args.qty),
    }) as HTMLInputElement;
    dec.disabled = args.qty <= 0;
    inc.disabled = !args.canInc;
    dec.addEventListener("click", () => args.onChange(Math.max(0, args.qty - 1)));
    inc.addEventListener("click", () => {
      if (args.canInc) args.onChange(args.qty + 1);
    });
    qtyInput.addEventListener("change", () => {
      const v = Math.max(0, Math.min(args.maxQty, Math.floor(Number(qtyInput.value) || 0)));
      args.onChange(v);
    });
    const row = el(
      "div",
      { class: "vendor-row" },
      name,
      weight,
      price,
      el("div", { class: "vendor-stepper" }, dec, qtyInput, inc),
      lineTotal,
    );
    bindItemTooltip(row, args.tooltipTemplate, args.tooltipInstance);
    return row;
  }
}
